import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { message } from "antd";
import {
  fetchTemplateWordDraft,
  openTemplateWordDraft,
  publishTemplateWordDraft,
} from "../../../../service/settings";

/*
 * Word templates are edited in real Word, not in a converted PDF.
 *
 * Formstack holds the template's file. "Edit in Word" asks the server to
 * place a copy in SharePoint (once) and opens it in Word Online — the same
 * way every matter document opens. Word autosaves to SharePoint; nothing
 * reaches Formstack until "Publish", because templates generate real client
 * letters and a half-finished sentence must not go live.
 */

const Wrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const Card = styled.div`
  width: min(560px, 100%);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 26px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$border};
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
`;

const Meta = styled.span`
  font-weight: 500;
  opacity: 0.85;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Button = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  border-radius: 9px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #f8fafc;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Primary = styled(Button)`
  background: #1e40af;
  border-color: #1e40af;
  color: #fff;
  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

const Publish = styled(Button)`
  background: #b45309;
  border-color: #b45309;
  color: #fff;
  &:hover:not(:disabled) {
    background: #d97706;
  }
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
`;

const TONES = {
  none: { bg: "#f8fafc", fg: "#475569", border: "#e2e8f0", label: "Not opened in Word yet" },
  in_sync: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0", label: "Formstack has the latest version" },
  draft_changed: { bg: "#fffbeb", fg: "#b45309", border: "#fde68a", label: "Edited in Word — not published yet" },
};

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

const WordDraftPanel = ({ formstackId, templateName, onPublished, beforePublish }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const alive = useRef(true);

  const refresh = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const res = await fetchTemplateWordDraft(formstackId);
        if (!alive.current) return;
        setStatus(res.data);
        setError("");
      } catch (err) {
        if (!alive.current) return;
        setError(errorText(err, "Could not check the Word copy of this template."));
      } finally {
        if (alive.current && !quiet) setLoading(false);
      }
    },
    [formstackId]
  );

  useEffect(() => {
    alive.current = true;
    refresh();
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  // Coming back from the Word tab: re-check quietly so the banner is honest.
  useEffect(() => {
    const onFocus = () => {
      if (status?.exists) refresh({ quiet: true });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh, status?.exists]);

  const handleOpen = useCallback(async () => {
    // Open the tab synchronously so pop-up blockers allow it, then point it.
    const tab = window.open("", "_blank");
    setOpening(true);
    try {
      const res = await openTemplateWordDraft(formstackId);
      // Point the tab even if this panel has since unmounted — the user is
      // waiting on that tab, not on us.
      if (tab) {
        tab.opener = null;
        tab.location = res.data.web_url;
      } else {
        window.open(res.data.web_url, "_blank", "noopener,noreferrer");
      }
      if (!alive.current) return;
      setStatus({ ...res.data, exists: true, available: true });
      setError("");
    } catch (err) {
      if (tab) tab.close();
      if (!alive.current) return;
      const text = errorText(err, "Could not open the template in Word.");
      setError(text);
      message.error(text);
    } finally {
      if (alive.current) setOpening(false);
    }
  }, [formstackId]);

  const handlePublish = useCallback(async () => {
    // Publishing reloads the editor; the host gets to protect unsaved work first.
    if (beforePublish && beforePublish() === false) return;
    const ok = window.confirm(
      `Publish "${templateName}" to Formstack?\n\nEvery document generated from now on will use the version saved in Word.`
    );
    if (!ok) return;
    setPublishing(true);
    try {
      const res = await publishTemplateWordDraft(formstackId);
      if (!alive.current) return;
      setStatus({ ...res.data, exists: true, available: true });
      setError("");
      message.success("Published. Formstack now has the version you edited in Word.");
      onPublished?.(res.data);
    } catch (err) {
      if (!alive.current) return;
      const text = errorText(err, "Publish failed — Formstack still has the previous version.");
      setError(text);
      message.error(text);
    } finally {
      if (alive.current) setPublishing(false);
    }
  }, [beforePublish, formstackId, onPublished, templateName]);

  if (loading) {
    return (
      <Wrap>
        <Card data-testid="word-panel">
          <Body>Checking the Word copy of this template…</Body>
        </Card>
      </Wrap>
    );
  }

  if (status && status.available === false) {
    return (
      <Wrap>
        <Card data-testid="word-panel">
          <Title>This is a Word template</Title>
          <Body data-testid="word-unavailable">
            Editing in Word needs WHG Docs&apos; SharePoint connection, which is not set up on this server. You can still
            download the file, edit it in Word, and use “Replace the file” under More.
          </Body>
        </Card>
      </Wrap>
    );
  }

  const state = status?.exists ? status.status : "none";
  const tone = TONES[state] || TONES.none;

  return (
    <Wrap>
      <Card data-testid="word-panel">
        <Title>This is a Word template — edit it in Word</Title>
        <Body>
          Word templates are written in real Word, not drawn on like a PDF form. Fields are tags such as{" "}
          <code>{"{$Client Full Name}"}</code>; signature spots are tags too. Edit the document in Word, then publish it so
          Formstack starts using the new version.
        </Body>

        <StatusRow $bg={tone.bg} $fg={tone.fg} $border={tone.border} data-testid="word-status" data-state={state}>
          <Dot />
          <span>{tone.label}</span>
          {status?.exists && status.last_modified_at && (
            <Meta>
              · last saved {when(status.last_modified_at)}
              {status.last_modified_by ? ` by ${status.last_modified_by}` : ""}
            </Meta>
          )}
        </StatusRow>

        {error && <Body style={{ color: "#b91c1c" }} data-testid="word-error">{error}</Body>}

        <Actions>
          <Primary type="button" onClick={handleOpen} disabled={opening || publishing} data-testid="word-open">
            {opening ? "Opening…" : status?.exists ? "Open in Word" : "Edit in Word"}
          </Primary>
          {state === "draft_changed" && (
            <Publish type="button" onClick={handlePublish} disabled={publishing || opening} data-testid="word-publish">
              {publishing ? "Publishing…" : "Publish to Formstack"}
            </Publish>
          )}
          {status?.exists && (
            <Button type="button" onClick={() => refresh()} disabled={publishing || opening} data-testid="word-refresh">
              Check for changes
            </Button>
          )}
        </Actions>

        {!status?.exists && (
          <Steps>
            <li>“Edit in Word” places a copy in SharePoint and opens it in Word Online.</li>
            <li>Make your changes; Word saves them as you go.</li>
            <li>Come back here and press “Publish to Formstack” when the template is ready.</li>
          </Steps>
        )}
        {status?.exists && status.published_at && (
          <Body style={{ fontSize: 12 }}>
            Last published {when(status.published_at)}
            {status.published_by ? ` by ${status.published_by}` : ""}.
          </Body>
        )}
      </Card>
    </Wrap>
  );
};

WordDraftPanel.propTypes = {
  formstackId: PropTypes.string.isRequired,
  templateName: PropTypes.string,
  onPublished: PropTypes.func,
  beforePublish: PropTypes.func,
};

export default WordDraftPanel;
